import type { ConnectorSummary, ReauthAddition } from "@/types/connectors.ts";

/**
 * The API path that re-runs a connector's OAuth consent.
 *
 * Returns a path rather than a URL, which is what makes it shareable: web
 * resolves it through `resolveApiPath`, native through its own API base.
 *
 * Google and Microsoft have dedicated reauth endpoints that refresh credentials
 * in place, preserving the connector row and everything hanging off it. Every
 * other OAuth provider has no such endpoint, and re-running the generic connect
 * flow refreshes its tokens instead.
 */
export const connectorReauthPath = (
  connector: Pick<ConnectorSummary, "id" | "provider">,
): string => {
  if (connector.provider === "link") return "/api/a/payments/link/setup";
  if (connector.provider === "google") {
    return `/api/a/auth/google/reauth/${connector.id}`;
  }
  if (connector.provider === "microsoft") {
    return `/api/a/auth/microsoft/reauth/${connector.id}`;
  }
  return `/api/a/auth/oauth/${connector.provider}/connect`;
};

/**
 * The re-auth that adds one kind of access to a Google or Microsoft connector,
 * asking for what it holds plus that access. A connector's `missingAccess`
 * names what it can add.
 */
export const connectorAddAccessPath = (
  connector: Pick<ConnectorSummary, "id" | "provider">,
  add: ReauthAddition,
): string => `${connectorReauthPath(connector)}?add=${add}`;

/** How Settings names each access a sign-in account lacks, and its connect. */
export const MISSING_ACCESS_LABELS: Record<ReauthAddition, { row: string; button: string }> = {
  email: { row: "Mail not connected", button: "Connect mail" },
  calendar: { row: "Calendar not connected", button: "Connect calendar" },
  contacts: { row: "Contacts not connected", button: "Connect contacts" },
};
